"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { ArrowRight, Sparkles, Hammer, ShieldCheck, Search, MoveHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RestorationShowcase() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max(x / rect.width * 100, 0), 100);
    setSliderPosition(percentage);
  }, []);
  const onMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    handleMove(e.clientX);
  };
  const onTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      handleMove(e.touches[0].clientX);
    };
    const onEnd = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("mouseup", onEnd);
      window.addEventListener("touchend", onEnd);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchend", onEnd);
    };
  }, [isDragging, handleMove]);
  const processSteps = [{
    title: "Assessment & Evaluation",
    description: "Careful inspection of the boat’s exterior to identify oxidation, buildup, and areas that require special attention.",
    icon: Search,
    code: "01"
  }, {
    title: "Hand Wash & Surface Prep",
    description: "Thorough hand wash of the hull, deck, and cockpit to safely remove dirt, salt residue, and surface contaminants.",
    icon: Hammer,
    code: "02"
  }, {
    title: "Polishing & Detailing",
    description: "Machine polishing process to reduce oxidation, restore gloss, and enhance the overall appearance of the surface.",
    icon: Sparkles,
    code: "03"
  }, {
    title: "Wax Protection",
    description: "Application of high-quality marine wax to protect the surface, improve shine, and extend the life of the finish.",
    icon: ShieldCheck,
    code: "04"
  }];
  return <section data-section-id="28316" id="our-professional-process" ref={sectionRef} className={`py-24 bg-background text-foreground overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-secondary/30 text-secondary-foreground text-xs font-mono tracking-widest mb-4 border border-border/50 uppercase">
            <Sparkles className="w-3 h-3 text-primary" />
            <span>Marine Precision Standards</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-default tracking-tight mb-4">
            Our Professional Process
          </h2>
          <p className="text-muted-foreground text-lg font-sans">
            Experience the Boat Bathers standard. Drag the slider to witness the transformation from weathered hull to showroom shine.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto mb-20 select-none group">
          <div ref={containerRef} className="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-sm overflow-hidden shadow-2xl border border-border/40 cursor-ew-resize" onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
            {}
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634081/img/yacht_deck_clean_after.png" alt="Restored yacht deck after cleaning" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-md px-4 py-1.5 rounded-sm text-xs font-bold tracking-widest uppercase shadow-sm border border-border/50 font-sans">
              Restored
            </div>

            {}
            <div className="absolute inset-0 w-full h-full overflow-hidden" style={{
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634081/img/yacht_deck_dirty_before.png" alt="Weathered yacht deck before cleaning" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-md px-4 py-1.5 rounded-sm text-xs font-bold tracking-widest uppercase shadow-sm border border-border/50 font-sans">
                Initial State
              </div>
            </div>

            {}
            <div className="absolute top-0 bottom-0 w-0.5 bg-primary cursor-ew-resize shadow-[0_0_15px_rgba(0,0,0,0.8)] z-10" style={{
            left: `${sliderPosition}%`
          }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-primary rounded-none shadow-xl flex items-center justify-center text-primary-foreground transition-transform hover:scale-110">
                <MoveHorizontal className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-center text-xs text-muted-foreground font-mono tracking-widest uppercase">
            <span className="flex items-center gap-3">
              <MoveHorizontal className="w-4 h-4 text-primary" />
              Slide to Compare Results
            </span>
          </div>
        </div>

        {}
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <Separator className="flex-1 bg-border/40" />
            <h3 className="text-2xl font-default tracking-tight px-4">Four Stages of Excellence</h3>
            <Separator className="flex-1 bg-border/40" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => <Card key={i} data-index={i} className="border-border/40 bg-card/40 backdrop-blur-md hover:bg-card/80 transition-all duration-500 rounded-sm group/card">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2.5 rounded-sm bg-primary/10 text-primary group-hover/card:bg-primary group-hover/card:text-primary-foreground transition-colors duration-300">
                      <step.icon className="w-5 h-5" />
                    </div>
                    <span className="text-4xl font-default text-muted-foreground/20 font-bold leading-none">
                      {step.code}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-default group-hover/card:text-primary transition-colors">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed font-sans text-muted-foreground">
                    {step.description}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>

          <div className="mt-16 text-center">
            <Button asChild size="lg" className="rounded-sm font-sans font-bold tracking-widest uppercase px-8 py-6 bg-primary hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/20">
              <Link to="#">
                Book Professional Restoration
                <ArrowRight className="ml-3 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}